import { ADMIN_ROUTES, ROLE, ROUTES } from 'constant'
import { loginSuccess, userLogin } from 'context/Auth/auth.slice'
import { useCallback, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { storageServices, authServices } from 'services'

const CREDENTIALS_ERROR_MESSAGE = 'Could not validate credentials'

function isLoginPage(): boolean {
  const { pathname } = window.location
  return pathname === ROUTES.LOGIN || pathname === ADMIN_ROUTES.LOGIN
}

export function useReAuthentication() {
  const dispatch = useDispatch()
  const [loading, setLoading] = useState(true)
  const [showTimeoutOverlay, setShowTimeoutOverlay] = useState(false)
  const refreshToken = storageServices.getRefreshToken()
  const isAdmin = storageServices.getRoleUser() === ROLE.ADMIN

  const reAuth = useCallback(
    async (role: ROLE) => {
      if (!refreshToken) return

      const stopLoading = () => setLoading(false)

      try {
        const res = await authServices.getAuth({
          refresh_token: refreshToken,
          role,
        })
        if (!res.is_success) {
          stopLoading()
          return
        }
        const { data } = res
        if (data.access_token) storageServices.setAccessToken(data.access_token)
        if (data.refresh_token)
          storageServices.setRefreshToken(data.refresh_token)
        dispatch(loginSuccess())
        dispatch(userLogin({ user: data }))
        stopLoading()
      } catch (err: unknown) {
        const message =
          err && typeof err === 'object' && 'response' in err
            ? (err as { response?: { data?: { message?: string } } }).response
                ?.data?.message
            : undefined

        if (message === CREDENTIALS_ERROR_MESSAGE) {
          storageServices.removeAccessToken()
          storageServices.removeRefreshToken()
          storageServices.removeRoleUser()
          window.location.assign(
            role === ROLE.ADVISOR ? ROUTES.LOGIN : ADMIN_ROUTES.LOGIN,
          )
        } else {
          setShowTimeoutOverlay(true)
        }
        stopLoading()
      }
    },
    [dispatch, refreshToken],
  )

  useEffect(() => {
    if (isLoginPage()) {
      setLoading(false)
      return
    }
    if (refreshToken) {
      reAuth(isAdmin ? ROLE.ADMIN : ROLE.ADVISOR)
    } else {
      setLoading(false)
    }
  }, [isAdmin, reAuth, refreshToken])

  return { loading, showTimeoutOverlay, reAuth }
}
